import React, { useState, useEffect } from 'react';
import { useGetCricketDetailQuery } from '../services/sports/sportsApi';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from '../components/ChangePasswordModal';

const SettingPage = () => {
  const navigate = useNavigate();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  
  // Get current date and 7 days ago for API call
  const getDateRange = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(sevenDaysAgo),
      to: formatDate(today)
    };
  };

  const { from, to } = getDateRange();
  
  // Call cricket detail API
  const { data: cricketData, isLoading, error } = useGetCricketDetailQuery({ from, to });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="w-full">
      {/* Top Buttons */}
      <div className="flex flex-wrap gap-4 mb-6">
        <button 
          onClick={() => navigate('/settings/search-user')}
          className="bg-[#1a3a40] text-white px-8 py-4 text-lg font-medium hover:bg-[#2a4a50] transition-colors"
        >
          Search User
        </button>
        <button 
          onClick={() => navigate('/settings/account-statement')}
          className="bg-[#1a3a40] text-white px-8 py-4 text-lg font-medium hover:bg-[#2a4a50] transition-colors"
        >
          Statements
        </button>
        <button 
          onClick={() => navigate('/settings/account-operations')}
          className="bg-[#1a3a40] text-white px-8 py-4 text-lg font-medium hover:bg-[#2a4a50] transition-colors"
        >
          A/c operations
        </button>
        <button className="bg-[#1a3a40] text-white px-8 py-4 text-lg font-medium hover:bg-[#2a4a50] transition-colors">
          Profit & Loss
        </button>
        {/* <button className="bg-[#1a3a40] text-white px-8 py-4 text-lg font-medium hover:bg-[#2a4a50] transition-colors">
          Casino Profit & Loss
        </button> */}
        <button 
          onClick={() => setIsChangePasswordModalOpen(true)}
          className="bg-[#1a3a40] text-white px-8 py-4 text-lg font-medium hover:bg-[#2a4a50] transition-colors"
        >
          Change Password
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg overflow-hidden shadow">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#1a3a40] text-white">
              <th className="border border-gray-400 p-3 text-left font-semibold">Title</th>
              <th className="border border-gray-400 p-3 text-left font-semibold">Sport</th>
              <th className="border border-gray-400 p-3 text-left font-semibold">Open Date</th>
              <th className="border border-gray-400 p-3 text-left font-semibold">Declared</th>
              <th className="border border-gray-400 p-3 text-left font-semibold">Won By</th>
              <th className="border border-gray-400 p-3 text-left font-semibold">Profit/Loss</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="6" className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <p className="mt-2 text-gray-600">Loading...</p>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-red-500">
                  Error loading data
                </td>
              </tr>
            ) : cricketData?.data && cricketData.data.length > 0 ? (
              cricketData.data.map((match) => (
                <tr key={match.eventId} className="hover:bg-gray-50 border-b border-gray-200">
                  <td className="border border-gray-400 p-3 text-sm whitespace-nowrap">{match.marketName || '-'}</td>
                  <td className="border border-gray-400 p-3 text-sm whitespace-nowrap">Cricket</td>
                  <td className="border border-gray-400 p-3 text-sm whitespace-nowrap">{formatDate(match.time)}</td>
                  <td className="border border-gray-400 p-3 text-sm whitespace-nowrap">
                    {match.isResult ? (
                      <span className="text-green-600 font-medium">Yes</span>
                    ) : (
                      <span className="text-red-600 font-medium">No</span>
                    )}
                  </td>
                  <td className="border border-gray-400 p-3 text-sm whitespace-nowrap">{match.wonBy || '-'}</td>
                  <td className="border border-gray-400 p-3 text-sm whitespace-nowrap">
                    <span className={match.profitLoss >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                      {match.profitLoss ? match.profitLoss.toFixed(2) : '0.00'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-16 text-center">
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <svg 
                      className="w-16 h-16 mb-4"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" 
                      />
                    </svg>
                    <span className="text-lg">No Data</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default SettingPage;
